import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  useFragment as useApolloFragment,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import { Box, Dialog, DialogContent } from '@mui/material';
import { Route, Routes, useSearchParams } from 'react-router-dom';

import { ConfirmationDialog } from '@modules/_shared/components/ConfirmationDialog';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import type { ReportComponentProps } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import {
  DrawerContainerProvider,
  NO_DRAWER_CONTAINER,
  useDrawerContainer,
} from '@modules/_shared/hooks/drawerContainerProvider';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  INSERT_CMA_REPORTS_COMPARABLES,
  STEP_LISTINGS_COMPARE_ALGO,
  STEP_LISTINGS_COMPARE_FRAGMENT,
  STEP_LISTINGS_COMPARE_LISTINGS,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import AggregatesListings from '@modules/reports/cma-reports/step-listing-compare/AggregatesListings';
import { replaceCmaReportComparableListings } from '@modules/reports/cma-reports/step-listing-compare/cacheUtils';
import ComparableListingsSection from '@modules/reports/cma-reports/step-listing-compare/ComparableListingsSection';
import ListingComparableDetails from '@modules/reports/cma-reports/step-listing-compare/ListingComparableDetails';
import { ListingsStepSkeleton } from '@modules/reports/cma-reports/step-listing-compare/ListingsStepSkeleton';
import type { CmaListingWithDistance } from '@modules/reports/cma-reports/step-listing-compare/types';
import { getAutoAlgoParams } from '@modules/reports/cma-reports/utils';
import { FooterActionsComponentWithoutForm } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';

const StepListingsCompare = (props: ReportComponentProps) => {
  const { reportId: cmaReportId } = props;
  const { t, countryCode } = useLocale();
  const drawerContainer = useDrawerContainer();
  const { me } = useAppData();
  const { track } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const [manualSelectionOpen, setManualSelectionOpen] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [listings, setListings] = useState<CmaListingWithDistance[]>([]);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const { data, complete } = useApolloFragment({
    fragment: STEP_LISTINGS_COMPARE_FRAGMENT,
    fragmentName: 'StepListingsCompare',
    from: {
      __typename: 'cma_reports',
      id: cmaReportId,
    },
  });

  const handleOpenManualSelection = useCallback(() => {
    setManualSelectionOpen(true);

    // We use setTimeout(0) to update the URL after React finishes updating the state
    // This fixes a bug where the 'where' parameter would get lost if we try to update
    // both state and URL at the same time
    setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);

      const whereParam = JSON.stringify({
        listing: {
          places: {
            id: {
              _in: data?.property?.locality_place?.id
                ? [data.property.locality_place.id]
                : [],
            },
          },
          property_main_type: {
            _in: data?.property?.property_type?.main_type
              ? [data.property.property_type.main_type]
              : [],
          },
        },
      });

      newParams.set('where', whereParam);
      setSearchParams(newParams);
    }, 0);
  }, [data, searchParams, setSearchParams]);

  const handleCloseManualSelection = useCallback(() => {
    // Clear all search parameters
    setSearchParams(new URLSearchParams());
    setManualSelectionOpen(false);
  }, [setSearchParams]);

  const listingsInCmaReport = useMemo(
    () => data?.comparable_listings?.filter(Boolean) ?? [],
    [data?.comparable_listings],
  );

  const {
    data: queryData,
    previousData,
    refetch,
    error: listingsError,
  } = useQuery(STEP_LISTINGS_COMPARE_LISTINGS, {
    skip: !complete,
    variables: {
      ids:
        listingsInCmaReport
          .map(listing => listing?.aggregates_db__merged_listings_clusters_id)
          .sort() ?? [],
      ch: countryCode === 'CH',
      es: countryCode === 'ES',
      fr: countryCode === 'FR',
      it: countryCode === 'IT',
      pl: countryCode === 'PL',
      pt: countryCode === 'PT',
      lat: data?.property?.lat ?? null,
      lng: data?.property?.lng ?? null,
    },
    context: {
      clientName: 'scrapers',
    },
  });

  useEffect(() => {
    if (listingsError) {
      setErrorLike(listingsError);
    }
  }, [listingsError]);

  useEffect(() => {
    const activeData = queryData ?? previousData;
    const comparableListings =
      activeData?.ch_get_cma_listings_distance ??
      activeData?.es_get_cma_listings_distance ??
      activeData?.fr_get_cma_listings_distance ??
      activeData?.it_get_cma_listings_distance ??
      activeData?.pl_get_cma_listings_distance ??
      activeData?.pt_get_cma_listings_distance ??
      [];

    const sortedListings = listingsInCmaReport
      .map(listing =>
        comparableListings.find(
          c =>
            c.listing?.id ===
            listing?.aggregates_db__merged_listings_clusters_id,
        ),
      )
      .filter((item): item is CmaListingWithDistance => item?.listing != null);
    setListings(sortedListings);
  }, [queryData, previousData, listingsInCmaReport]);

  const [insertComparable] = useMutation(INSERT_CMA_REPORTS_COMPARABLES);

  const autoAlgoParams = useMemo(
    () => getAutoAlgoParams(data?.property, countryCode),
    [data?.property, countryCode],
  );
  const [runAutomaticAlgo, { loading: runningAutomaticAlgo }] = useLazyQuery(
    STEP_LISTINGS_COMPARE_ALGO,
    { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
  );

  const generateComparable = useCallback(() => {
    const source = data?.report_source;
    const propertyId = data?.property?.id;
    const leadId = data?.lead?.id;
    const startTime = Date.now();

    runAutomaticAlgo(autoAlgoParams).then(({ data: listingsData }) => {
      const listings =
        listingsData?.ch_get_similar_listings_cohorts ??
        listingsData?.es_get_similar_listings_cohorts ??
        listingsData?.fr_get_similar_listings_cohorts ??
        listingsData?.it_get_similar_listings_cohorts ??
        listingsData?.pl_get_similar_listings_cohorts ??
        listingsData?.pt_get_similar_listings_cohorts ??
        [];
      insertComparable({
        variables: {
          id: cmaReportId,
          objects: listings.map(listing => ({
            aggregates_db__merged_listings_clusters_id: listing.listing?.id,
            cma_report_id: cmaReportId,
          })),
        },
        update: (cache, { data: updatedData }) =>
          replaceCmaReportComparableListings(cache, cmaReportId, updatedData),
        onCompleted: savedData => {
          const count =
            savedData.insert_cma_reports_comparables?.returning.length;
          const baseProperties = getBaseCmaEventProperties(me, cmaReportId);
          if (baseProperties) {
            track(ANALYTICS_EVENTS.CMA_LISTINGS_AUTO_SEARCH, {
              ...baseProperties,
              source,
              search_duration_ms: Date.now() - startTime,
              ...(count !== undefined && { count }),
              ...(propertyId && { property_id: propertyId }),
              ...(leadId && { lead_id: leadId }),
            });
          }
        },
      });
    });
  }, [
    data?.report_source,
    data?.property?.id,
    data?.lead?.id,
    autoAlgoParams,
    runAutomaticAlgo,
    insertComparable,
    cmaReportId,
    me,
    track,
  ]);

  const handleClickConfirmDialog = useCallback(() => {
    generateComparable();
    setRefreshOpen(false);
  }, [generateComparable]);

  if (!complete) {
    return <ListingsStepSkeleton />;
  }

  return (
    <>
      <Dialog
        open={manualSelectionOpen}
        fullScreen
        css={{
          '.MuiDialog-paperFullScreen': {
            maxWidth: 'max(800px, calc(100vw - 156px))',
          },
        }}
        onClose={handleCloseManualSelection}
        slots={{
          transition: SlideUpTransition,
        }}
      >
        <DialogContent
          sx={{
            padding: '0 !important',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AggregatesListings
            data={data}
            cmaReportId={cmaReportId}
            refetch={refetch}
            onClose={handleCloseManualSelection}
          />
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={refreshOpen}
        onClose={() => setRefreshOpen(false)}
        onConfirm={handleClickConfirmDialog}
        title={t('Refresh Comparable Listings')}
        message={t('Refresh Comparable Listings Confirmation')}
      />
      <Box
        css={{
          flexGrow: 1,
          position: 'relative',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <ComparableListingsSection
          setManualSelectionOpen={handleOpenManualSelection}
          setRefreshOpen={setRefreshOpen}
          cmaReportId={cmaReportId}
          isRunningAutomaticAlgo={runningAutomaticAlgo}
          generateComparable={generateComparable}
          cmaListingDistance={listings}
          setListings={setListings}
          isPropertyComparableListingsSquareMeterCalculation={
            data?.is_property_comparable_listings_square_meter_calculation ??
            false
          }
          isShowFullIdxDescription={data?.show_full_idx_description ?? false}
          includeComparableListingDetails={
            data?.include_comparable_listing_details ?? true
          }
          showListingCards={data?.show_comparable_listing_cards ?? false}
          comparableListingsMapZoomLevel={
            data?.comparable_listings_map_zoom_level ?? null
          }
        />

        <FooterActionsComponentWithoutForm updating={false} {...props} />
      </Box>
      <Routes>
        <Route
          path=":encodedListingId"
          element={
            <DrawerContainerProvider
              value={
                manualSelectionOpen ? NO_DRAWER_CONTAINER : drawerContainer
              }
            >
              <ListingComparableDetails />
            </DrawerContainerProvider>
          }
        />
      </Routes>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};

export default StepListingsCompare;
